import React, { useState } from 'react';
import { generateTextToVideo, generateImageToVideo } from '../services/apiService';

const GenerationForm = ({ onGenerationSubmit }) => {
  const [prompt, setPrompt] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [generationType, setGenerationType] = useState('text-to-video');
  const [resolution, setResolution] = useState('720p');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Advanced parameters with defaults from OpenAPI spec
  const [advancedParams, setAdvancedParams] = useState({
    video_length: 5,
    num_steps: 16,
    seed: -1,
    use_prompt_extend: false,
    size: [1280, 720]
  });

  const handleAdvancedParamChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Handle different input types
    const processedValue = type === 'checkbox'
      ? checked
      : type === 'number'
        ? parseFloat(value)
        : value;

    setAdvancedParams({
      ...advancedParams,
      [name]: processedValue
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!prompt.trim()) return;

    setLoading(true);
    setError(null);

    try {
      let response;
      let type;

      // Filter out empty advanced params
      const filteredAdvancedParams = Object.fromEntries(
        Object.entries(advancedParams).filter(([_, value]) =>
          value !== '' && value !== null && value !== undefined
        )
      );

      // Remove size and resolution parameters for image-to-video generation
      if (generationType === 'image-to-video') {
        delete filteredAdvancedParams.size;
        delete filteredAdvancedParams.resolution;
      }

      console.log('Submitting with prompt:', prompt);
      console.log('Advanced parameters:', filteredAdvancedParams);

      if (generationType === 'text-to-video') {
        response = await generateTextToVideo(prompt, filteredAdvancedParams);
        type = 'text-to-video';
      } else {
        if (!imageUrl) {
          throw new Error('Please enter an image URL');
        }

        response = await generateImageToVideo(imageUrl, prompt, resolution, filteredAdvancedParams);
        type = 'image-to-video';
      }

      console.log('API response:', response);

      // Extract job_id from the response
      const jobId = response.job_id;

      if (jobId) {
        onGenerationSubmit({
          jobId,
          prompt,
          imageUrl: generationType === 'image-to-video' ? imageUrl : null,
          type,
          timestamp: new Date().toISOString()
        });
      } else {
        throw new Error('No job ID returned from the server');
      }
    } catch (err) {
      console.error('Generation error:', err);
      setError(err.message || 'Failed to start generation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="generation-form-container">
      <h2>Create New Video</h2>

      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}

      <form className="generation-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="generationType">Generation Type</label>
          <select
            id="generationType"
            value={generationType}
            onChange={(e) => setGenerationType(e.target.value)}
            className="form-select"
          >
            <option value="text-to-video">Text to Video</option>
            <option value="image-to-video">Image to Video</option>
          </select>
        </div>

        {generationType === 'image-to-video' && (
          <>
            <div className="form-group">
              <label htmlFor="imageUrl">Image URL</label>
              <input
                type="text"
                id="imageUrl"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="Enter URL of the image"
                required={generationType === 'image-to-video'}
              />
            </div>
          </>
        )}

        <div className="form-group">
          <label htmlFor="prompt">Prompt</label>
          <textarea
            id="prompt"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Describe what you want in the video..."
            rows={4}
            required
          />
        </div>

        <div className="advanced-toggle">
          <button
            type="button"
            className="toggle-button"
            onClick={() => setShowAdvanced(!showAdvanced)}
          >
            {showAdvanced ? 'Hide Advanced Options' : 'Show Advanced Options'}
          </button>
        </div>

        {showAdvanced && (
          <div className="advanced-options">
            <h3>Advanced Options</h3>

            <div className="form-row">
              <div className="form-group half">
                <label htmlFor="video_length">Video Length</label>
                <input
                  type="number"
                  id="video_length"
                  name="video_length"
                  value={advancedParams.video_length}
                  onChange={handleAdvancedParamChange}
                  min="1"
                  max="10"
                />
                <small>The length of the video in seconds</small>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group half">
                <label htmlFor="num_steps">Sampling Steps</label>
                <input
                  type="number"
                  id="num_steps"
                  name="num_steps"
                  value={advancedParams.num_steps}
                  onChange={handleAdvancedParamChange}
                  min="1"
                  max="64"
                />
                <small>Higher values = better quality but slower (max 64), should be either 8, 16, 32, or 64.</small>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="seed">Random Seed</label>
                <input
                  type="number"
                  id="seed"
                  name="seed"
                  value={advancedParams.seed}
                  onChange={handleAdvancedParamChange}
                />
                <small>-1 for random seed, or specify for reproducible results</small>
              </div>
            </div>

            {generationType === 'text-to-video' && (
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="size">Video Size</label>
                  <select
                    id="size"
                    name="size"
                    value={JSON.stringify(advancedParams.size)}
                    onChange={(e) => {
                      const newSize = JSON.parse(e.target.value);
                      setAdvancedParams({
                        ...advancedParams,
                        size: newSize
                      });
                    }}
                  >
                    <option value={JSON.stringify([832, 480])}>480p (832×480)</option>
                    <option value={JSON.stringify([1280, 720])}>720p (1280×720)</option>
                    <option value={JSON.stringify([1024, 1024])}>Square (1024×1024)</option>
                  </select>
                  <small>Resolution of the generated video</small>
                </div>
              </div>
            )}

            <div className="form-row">
              <div className="form-group checkbox">
                <label>
                  <input
                    type="checkbox"
                    name="use_prompt_extend"
                    checked={advancedParams.use_prompt_extend}
                    onChange={handleAdvancedParamChange}
                  />
                  Use Prompt Extension
                </label>
                <small>Automatically enhance your prompt</small>
              </div>
            </div>
          </div>
        )}

        <div className="form-actions">
          <button
            type="submit"
            className="submit-button"
            disabled={loading || (generationType === 'image-to-video' && !imageUrl)}
          >
            {loading ? 'Generating...' : 'Generate Video'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default GenerationForm;
